/**
 * R267d · Feb 2026 — Settings → Communications.
 *
 * Only knob currently exposed: the workspace-wide default country
 * code used by the bulk-WA preflight fixer. Once set, the "Fix
 * with country code" button inside the preflight card pre-fills
 * this value and never has to prompt for `+91` again.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Save, Loader2, Phone, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { apiErrorMessage } from "@/lib/apiError";

export default function CommunicationsSettingsSection() {
  const { activeWorkspaceId, activeWorkspace, user } = useAuth();
  const role = activeWorkspace?.role || activeWorkspace?.membership_role;
  const isAdmin = user?.is_super_admin || role === "owner" || role === "admin";

  const [cc, setCc] = useState("");
  const [saved, setSaved] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/settings/communications`);
      setCc(data?.default_country_code || "");
      setSaved(data?.default_country_code || "");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load communications settings"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/workspaces/${activeWorkspaceId}/settings/communications`,
        { default_country_code: cc });
      setCc(data?.default_country_code || "");
      setSaved(data?.default_country_code || "");
      toast.success(data?.default_country_code
        ? `Default country code saved as ${data.default_country_code}`
        : "Default country code cleared");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const changed = String(cc || "") !== String(saved || "");

  if (!isAdmin) {
    return (
      <div className="border border-border p-6 text-sm text-muted-foreground"
           data-testid="settings-comms-forbidden">
        Only workspace owners/admins can view or change communications defaults.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-comms-section">
      <div className="border border-border p-6 space-y-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-primary" />
          <div className="label-eyebrow">Bulk WhatsApp preflight</div>
        </div>
        <p className="text-sm text-muted-foreground -mt-2">
          Set a workspace-wide default country code. The preflight
          "Fix with country code" button (shown when leads are
          missing a <span className="font-mono">+</span> prefix) will
          pre-fill this value so ops never have to type it in again.
          Leave blank to keep prompting per-send.
        </p>
        <div className="flex items-end gap-3">
          <div className="w-48">
            <Label className="label-eyebrow mb-2 inline-block">
              Default country code
            </Label>
            <div className="flex items-center gap-1">
              <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
              <Input
                value={cc}
                placeholder="+91"
                onChange={(e) => setCc(e.target.value)}
                disabled={loading}
                className="h-10 font-mono"
                data-testid="settings-comms-cc-input"
              />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              E.164 format · e.g. <span className="font-mono">+91</span> (India),
              <span className="font-mono ml-1">+971</span> (UAE),
              <span className="font-mono ml-1">+968</span> (Oman)
            </div>
          </div>
          <Button
            onClick={save}
            disabled={saving || !changed}
            className="h-10 rounded-none"
            data-testid="settings-comms-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          {saved && (
            <div className="text-xs text-muted-foreground pb-2.5">
              Current: <span className="font-mono font-semibold">{saved}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
